'use client';
import { AppShell } from '@/components/layout/AppShell';
import { StatCard } from '@/components/common/StatCard';
import { Grid, Card, CardContent, Typography, Box, LinearProgress, Divider } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatBRL, formatHL } from '@/lib/format';
import type { SeasonCurrentAccountItemDto } from '@/types/api';
import { useSeasons } from '@/hooks/useReferenceData';
import { LoadingState } from '@/components/common/LoadingState';

export default function DashboardPage() {
  const { data: seasons } = useSeasons();
  const currentSeason = seasons?.find(s => s.isActive) ?? seasons?.[0];

  const { data: accounts, isLoading } = useQuery<SeasonCurrentAccountItemDto[]>({
    queryKey: ['current-account', 'season', currentSeason?.id],
    queryFn: () => api.get(`/Reports/current-account/season/${currentSeason!.id}`).then(r => r.data),
    enabled: !!currentSeason?.id,
  });

  const totalContracted = accounts?.reduce((s, a) => s + a.totalContracted, 0) ?? 0;
  const totalValorRecebido = accounts?.reduce((s, a) => s + a.totalValorRecebido, 0) ?? 0;
  const totalPagoRs = accounts?.reduce((s, a) => s + a.totalPagoRs, 0) ?? 0;
  const totalSaldo = accounts?.reduce((s, a) => s + a.saldo, 0) ?? 0;
  const totalContracts = accounts?.reduce((s, a) => s + a.totalContracts, 0) ?? 0;
  const entregaPercent = totalContracted > 0 ? (totalValorRecebido / totalContracted) * 100 : 0;

  const saldoLabel = totalSaldo > 0.005 ? 'Saldo a Receber' : totalSaldo < -0.005 ? 'Saldo a Pagar' : 'Quitado';
  const saldoColor = totalSaldo > 0.005 ? 'warning.main' : totalSaldo < -0.005 ? 'error.main' : 'success.main';

  return (
    <AppShell title={`Dashboard — ${currentSeason?.name ?? '...'}`}>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Total Contratado"
            value={formatBRL(totalContracted)}
            icon={<DescriptionIcon sx={{ fontSize: 32 }} />}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Recebido (castanha)"
            value={formatBRL(totalValorRecebido)}
            icon={<Inventory2Icon sx={{ fontSize: 32 }} />}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Pago (adiantamentos)"
            value={formatBRL(totalPagoRs)}
            icon={<AttachMoneyIcon sx={{ fontSize: 32 }} />}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label={saldoLabel}
            value={formatBRL(Math.abs(totalSaldo))}
            icon={<AccountBalanceWalletIcon sx={{ fontSize: 32 }} />}
            color={saldoColor}
          />
        </Grid>
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" fontWeight={600}>Entrega da Safra ({totalContracts} contratos)</Typography>
            <Typography variant="body2" color="text.secondary">{entregaPercent.toFixed(1)}% entregue</Typography>
          </Box>
          <LinearProgress variant="determinate" value={Math.min(entregaPercent, 100)} sx={{ height: 10, borderRadius: 5 }} />
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ pb: '16px !important' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Conta-Corrente por Fornecedor</Typography>
          {isLoading && <LoadingState />}
          {accounts?.map((acc, i) => {
            const label = acc.saldo > 0.005 ? 'a receber' : acc.saldo < -0.005 ? 'a pagar' : 'quitado';
            const color = acc.saldo > 0.005 ? 'warning.main' : acc.saldo < -0.005 ? 'error.main' : 'success.main';
            return (
              <Box key={acc.supplierId}>
                {i > 0 && <Divider sx={{ my: 1.5 }} />}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={600}>{acc.supplierName}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatHL(acc.totalRecebidoHl)} recebido · {formatBRL(acc.totalValorRecebido)} / {formatBRL(acc.totalContracted)}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(acc.entregaPercent, 100)}
                      sx={{ height: 6, borderRadius: 3 }}
                      color={acc.entregaPercent >= 100 ? 'success' : 'primary'}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ minWidth: 110, textAlign: 'right', color, fontWeight: 600 }}>
                    {formatBRL(Math.abs(acc.saldo))} ({label})
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </CardContent>
      </Card>
    </AppShell>
  );
}
